import { FilterRow } from "@/components/shared/filter-bar";
import { FilterPanel, selectedLabels } from "@/components/shared/filter-panel";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { SearchInput } from "@/components/shared/search-input";
import { EntityComboboxMulti } from "@/components/ui/entity-combobox";
import { useEmploymentTypeOptions } from "@/hooks/use-employment-types";
import { useEntityOptionLabels } from "@/hooks/use-entity-options";
import {
  activeTeamFilterCount,
  EMPTY_TEAM_FILTERS,
  hasTeamFilters,
  type TeamFilters,
} from "@/lib/persons/team-filters";

/** Resolved labels for a collapsed summary chip; a count while they load. */
function joinLabels(data: { label: string }[] | undefined, ids: string[]): string {
  return data?.length ? data.map((o) => o.label).join(", ") : `${ids.length} selected`;
}

/**
 * Filters for the team report's company view (HR feedback, 2026-09-22).
 *
 * **Company scope only.** A manager's own team is small enough to read whole,
 * and the report opens on it; the 163-row company roster is the one that needed
 * narrowing. The caller decides — this component just renders what it is given.
 *
 * State lives in the caller rather than the URL, matching the scope switch
 * beside it. The page has no pagination or sort to serialize, and the directory
 * pages that do put filters in the URL have to freeze their origin breadcrumb
 * around the history rewriting it brings.
 *
 * Pillar → Department → Role Team cascade the same way they do on the People
 * directory: each list is scoped to what is selected above it.
 */
export function TeamFilterPanel({
  filters,
  onChange,
  search,
  onSearchChange,
}: {
  filters: TeamFilters;
  onChange: (next: TeamFilters) => void;
  search: string;
  onSearchChange: (next: string) => void;
}) {
  const employmentTypes = useEmploymentTypeOptions();
  const pillarLabels = useEntityOptionLabels("pillars", filters.pillarIds);
  const departmentLabels = useEntityOptionLabels("departments", filters.departmentIds);
  const roleTeamLabels = useEntityOptionLabels("role-teams", filters.roleTeamIds);
  const roleLabels = useEntityOptionLabels("roles", filters.roleIds);

  function set<K extends keyof TeamFilters>(key: K, value: TeamFilters[K]) {
    onChange({ ...filters, [key]: value });
  }

  const summary: string[] = [];
  if (filters.pillarIds.length)
    summary.push(`Pillar: ${joinLabels(pillarLabels.data, filters.pillarIds)}`);
  if (filters.departmentIds.length)
    summary.push(`Department: ${joinLabels(departmentLabels.data, filters.departmentIds)}`);
  if (filters.roleTeamIds.length)
    summary.push(`Role Team: ${joinLabels(roleTeamLabels.data, filters.roleTeamIds)}`);
  if (filters.roleIds.length)
    summary.push(`Job role: ${joinLabels(roleLabels.data, filters.roleIds)}`);
  if (filters.employmentTypes.length)
    summary.push(
      `Employment Type: ${selectedLabels(employmentTypes.options, filters.employmentTypes)}`,
    );

  return (
    <div className="space-y-2">
      <FilterPanel
        activeCount={activeTeamFilterCount(filters)}
        summary={summary}
        onReset={() => {
          onChange(EMPTY_TEAM_FILTERS);
          onSearchChange("");
        }}
        canReset={hasTeamFilters(filters, search)}
      >
        <FilterRow label="Pillar">
          <EntityComboboxMulti
            entity="pillars"
            className="w-full sm:w-72"
            value={filters.pillarIds}
            placeholder="All pillars"
            onChange={(ids) => set("pillarIds", ids)}
          />
        </FilterRow>
        <FilterRow label="Department">
          <EntityComboboxMulti
            entity="departments"
            className="w-full sm:w-72"
            value={filters.departmentIds}
            placeholder="All departments"
            // Scoped to the selected pillar(s); empty means every pillar.
            filters={
              filters.pillarIds.length ? { pillarId: filters.pillarIds.join(",") } : undefined
            }
            onChange={(ids) => set("departmentIds", ids)}
          />
        </FilterRow>
        <FilterRow label="Role Team">
          <EntityComboboxMulti
            entity="role-teams"
            className="w-full sm:w-72"
            value={filters.roleTeamIds}
            placeholder="All role teams"
            filters={
              filters.departmentIds.length
                ? { departmentId: filters.departmentIds.join(",") }
                : undefined
            }
            onChange={(ids) => set("roleTeamIds", ids)}
          />
        </FilterRow>
        {/* Job role is the role someone is ASSIGNED, which is a different thing
            from the Role Team they sit in — a person can hold two, and both are
            worth filtering by, so both are offered. */}
        <FilterRow label="Job role">
          <EntityComboboxMulti
            entity="roles"
            className="w-full sm:w-72"
            value={filters.roleIds}
            placeholder="Any job role"
            onChange={(ids) => set("roleIds", ids)}
          />
        </FilterRow>
        <FilterRow label="Employment Type">
          <FilterToggleGroup
            options={employmentTypes.options}
            value={filters.employmentTypes}
            onChange={(v) => set("employmentTypes", v)}
          />
        </FilterRow>
      </FilterPanel>
      <SearchInput
        value={search}
        onValueChange={onSearchChange}
        placeholder="Search by name or job title"
        className="sm:max-w-xs"
      />
    </div>
  );
}
